/**
 * OverdueCustomersPanel — top customers ranked by outstanding amount.
 *
 * Reads from `reports.aging.byCustomer`. The aging aggregator already
 * sorts by oldest-days desc; we cap at 4 rows for the dashboard glance
 * + link to `/sales/collections` for the full case list.
 *
 * Each row's age track bar visualises `oldestDays` against a 90-day
 * ceiling — the same severity ramp the prototype uses (`.row.is-severe`
 * at ≥60d, `.is-warn` at 30-60d, `.is-info` < 30d).
 *
 * @see prototype/home/dashboard.html `.row` blocks — V1.1 baseline
 */
import { Send, MessageCircle, FileText, ArrowRight } from "lucide-react";
import { Card, cn } from "@flint/ui";
import { deriveInitials, paiseToInrShort } from "../../../lib/format.ts";

/**
 * Mirror of `reports.aging.byCustomer[number]` — duplicated here so this
 * component doesn't depend on `@flint/api-contract` shapes by type
 * import (the contract package's types are re-exported via tRPC inference
 * downstream; declaring locally keeps the component's interface stable).
 */
export interface OverdueCustomerRow {
  customerId: string;
  customerName: string;
  outstandingPaise: string;
  oldestDays: number;
  invoiceCount: number;
}
export interface OverdueCustomersPanelProps {
  rows: readonly OverdueCustomerRow[];
  /** When true, render the empty-state copy in place of the rows. */
  isEmpty?: boolean;
}

const MAX_ROWS = 4;

export function OverdueCustomersPanel({ rows, isEmpty }: OverdueCustomersPanelProps) {
  const visible = rows.slice(0, MAX_ROWS);
  return (
    <Card className="min-w-0">
      <header className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-line flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-ink">Top overdue customers</h3>
          <p className="text-xs text-muted">Who needs attention first.</p>
        </div>
        <a
          href="/sales/collections"
          className="inline-flex items-center gap-1 h-8 px-3 rounded-sm border border-line bg-paper text-xs font-medium text-ink-2 hover:bg-bg"
        >
          View all
          <ArrowRight size={11} aria-hidden />
        </a>
      </header>

      {isEmpty || visible.length === 0 ? (
        <div className="px-6 py-8 text-center text-muted">
          <p className="text-sm font-semibold text-ink mb-1">No overdue customers.</p>
          <p className="text-xs">
            Everyone is inside their payment window — that won’t last forever, but enjoy it for now.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {visible.map((row, i) => (
            <OverdueRow key={row.customerId} row={row} seatIndex={i} />
          ))}
        </ul>
      )}
    </Card>
  );
}

// ── individual row ─────────────────────────────────────────────────

const AVATAR_TONES = ["bg-sky-600", "bg-bad", "bg-ok", "bg-warn"] as const;

interface OverdueRowProps {
  row: OverdueCustomerRow;
  seatIndex: number;
}

function OverdueRow({ row, seatIndex }: OverdueRowProps) {
  // Severity matches the prototype's three-tier ramp; capped at 90d so
  // longer-overdue customers still show a full bar without overflow.
  const severity: "severe" | "warn" | "info" =
    row.oldestDays >= 60 ? "severe" : row.oldestDays >= 30 ? "warn" : "info";
  const pct = Math.min(Math.round((row.oldestDays / 90) * 100), 100);
  // Action verb adapts to severity — call earliest, nudge later.
  const ActionIcon = severity === "severe" ? Send : severity === "warn" ? MessageCircle : FileText;
  const actionLabel =
    severity === "severe"
      ? "Send reminder"
      : severity === "warn"
        ? "Nudge on WhatsApp"
        : "See ledger";
  return (
    <li className="grid grid-cols-[36px_minmax(0,1fr)_auto_auto_auto] items-center gap-3 px-5 py-3 hover:bg-raised transition-colors">
      <span
        aria-hidden
        className={cn(
          "inline-grid place-items-center w-8 h-8 rounded text-xs font-bold text-paper",
          AVATAR_TONES[seatIndex % AVATAR_TONES.length],
        )}
      >
        {deriveInitials(row.customerName)}
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs font-semibold text-ink truncate" title={row.customerName}>
            {row.customerName}
          </span>
          <AgeChip days={row.oldestDays} severity={severity} />
        </div>
        <p className="text-xs text-muted truncate">
          {row.invoiceCount} open invoice{row.invoiceCount === 1 ? "" : "s"}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-[90px] h-1 bg-bg rounded-full overflow-hidden">
          <div
            className={cn(
              "h-full rounded-full",
              severity === "severe" ? "bg-bad" : severity === "warn" ? "bg-warn" : "bg-sky-600",
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <span className="num font-semibold text-xs text-right min-w-[64px] tabular-nums">
        {paiseToInrShort(row.outstandingPaise)}
      </span>
      <a
        href={`/sales/customers/${row.customerId}`}
        className="inline-flex items-center gap-1 h-8 px-3 rounded-sm border border-line bg-paper text-xs font-medium text-ink-2 hover:bg-bg whitespace-nowrap"
      >
        <ActionIcon size={11} aria-hidden />
        {actionLabel}
      </a>
    </li>
  );
}

function AgeChip({ days, severity }: { days: number; severity: "severe" | "warn" | "info" }) {
  const tone =
    severity === "severe"
      ? "bg-bad-50 text-bad"
      : severity === "warn"
        ? "bg-warn-50 text-warn"
        : "bg-sky-50 text-sky-700";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-bold uppercase tracking-wider",
        tone,
      )}
    >
      <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-current" />
      {days}d
    </span>
  );
}
